import {
  ELEMENTS_ACTION_CHANNEL,
  ElementsActionMessageType,
  createElementsActionWakeMessage,
  type ElementsActionInitMessage,
  type ElementsActionWakeSource,
  isElementsActionErrorMessage,
  isElementsActionFrameReadyMessage,
  isElementsActionPhaseMessage,
  isElementsActionResultMessage,
} from './action-protocol';
import { ELEMENTS_ACTION_SHELL_POLICY } from './action-shell-policy';

type TopLayerHost = HTMLDivElement & {
  showPopover?: () => void;
  hidePopover?: () => void;
};

type IsolatedSibling = {
  element: HTMLElement;
  inert: boolean;
  ariaHidden: string | null;
  tabIndex: string | null;
};

let scrollLocks = 0;
let bodyOverflow = '';
let documentOverflow = '';

// A group can own both Payment and Express Checkout Elements. Reference-count
// the document lock so closing one action cannot unlock scrolling underneath a
// second action that is still active.
const lockScroll = (): void => {
  if (scrollLocks === 0) {
    bodyOverflow = document.body.style.overflow;
    documentOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  }
  scrollLocks += 1;
};

const unlockScroll = (): void => {
  if (scrollLocks === 0) return;
  scrollLocks -= 1;
  if (scrollLocks === 0) {
    document.body.style.overflow = bodyOverflow;
    document.documentElement.style.overflow = documentOverflow;
  }
};

const restoreAttribute = (element: HTMLElement, name: string, value: string | null): void => {
  if (value === null) element.removeAttribute(name);
  else element.setAttribute(name, value);
};

export type ElementsActionRequest = {
  id: string;
  type: 'three_d_secure';
  source?: 'component' | 'payment_confirmation';
};

/**
 * Owns the accessibility and stacking boundary in the merchant document.
 *
 * An iframe embedded in a merchant container can be clipped, transformed, or
 * covered by merchant UI, and it cannot make its ancestor page inert. This host
 * is therefore inserted directly under <body>, promoted to the browser top
 * layer when Popover is available, and backed by a documented z-index fallback.
 * It snapshots merchant accessibility state so cleanup restores values instead
 * of assuming Paypercut was the only code touching the page.
 */
class ElementsActionHost {
  private host: TopLayerHost | null = null;
  private active = false;
  private isolatedSiblings = new Map<HTMLElement, IsolatedSibling>();
  private isolationObserver: MutationObserver | null = null;

  mount(): TopLayerHost {
    const host = this.ensureHost();
    // Reparenting a connected iframe can reload it. Promotion from the silent
    // transport to the visible action surface must therefore reuse the exact
    // body child instead of prepending it a second time.
    if (host.parentElement !== document.body) document.body.prepend(host);
    // A closed popover is forced to `display: none` by the browser. Remove the
    // attribute while bootstrapping so the Hosted Checkout frame has a real
    // viewport and Basis Theory can decide whether ACS/OOB content is needed.
    host.removeAttribute('popover');
    host.hidden = false;
    host.style.display = 'block';
    host.style.visibility = 'visible';
    host.style.opacity = '0';
    host.style.pointerEvents = 'none';
    return host;
  }

  open(): TopLayerHost {
    const host = this.mount();
    host.style.opacity = '1';
    host.style.pointerEvents = 'auto';

    if (!this.active) {
      this.active = true;
      lockScroll();
      this.isolateMerchantPage(host);
    }

    host.setAttribute('popover', 'manual');
    if (typeof host.showPopover === 'function' && !host.matches(':popover-open')) {
      try {
        host.showPopover();
      } catch {
        host.removeAttribute('popover');
      }
    }

    window.requestAnimationFrame(() => {
      if (this.active) host.style.background = ELEMENTS_ACTION_SHELL_POLICY.backdropVisible;
    });
    return host;
  }

  beginClose(): void {
    if (!this.host) return;
    this.host.style.background = ELEMENTS_ACTION_SHELL_POLICY.backdropHidden;
    this.host.style.opacity = '0';
    this.host.style.pointerEvents = 'none';
  }

  finishClose(): void {
    const host = this.host;
    if (!host) return;
    try {
      host.hidePopover?.();
    } catch {
      // A detached or already hidden popover needs no further cleanup.
    }
    host.hidden = true;
    host.style.display = 'none';
    host.style.visibility = 'visible';
    host.style.opacity = '0';
    host.style.pointerEvents = 'none';
    host.style.background = ELEMENTS_ACTION_SHELL_POLICY.backdropHidden;
    if (this.active) {
      this.active = false;
      this.restoreMerchantPage();
      unlockScroll();
    }
  }

  destroy(): void {
    this.finishClose();
    this.host?.remove();
    this.host = null;
  }

  private ensureHost(): TopLayerHost {
    if (this.host) return this.host;
    const host = document.createElement('div') as TopLayerHost;
    host.dataset.paypercutElementsActionHost = '';
    host.tabIndex = -1;
    host.hidden = true;
    Object.assign(host.style, {
      position: 'fixed',
      inset: '0',
      width: '100%',
      height: '100%',
      minWidth: '100%',
      minHeight: '100%',
      maxWidth: 'none',
      maxHeight: 'none',
      margin: '0',
      padding: '0',
      border: '0',
      display: 'none',
      visibility: 'visible',
      opacity: '0',
      pointerEvents: 'none',
      overflow: 'hidden',
      background: ELEMENTS_ACTION_SHELL_POLICY.backdropHidden,
      colorScheme: 'light only',
      transition: `background ${ELEMENTS_ACTION_SHELL_POLICY.hostTransitionMs}ms ease-in-out, opacity ${ELEMENTS_ACTION_SHELL_POLICY.hostTransitionMs}ms ease-in-out`,
      willChange: 'background, opacity',
      zIndex: String(ELEMENTS_ACTION_SHELL_POLICY.fallbackZIndex),
    });
    this.host = host;
    return host;
  }

  private isolateMerchantPage(host: HTMLElement): void {
    const isolate = (element: HTMLElement): void => {
      if (element === host || this.isolatedSiblings.has(element)) return;
      this.isolatedSiblings.set(element, {
        element,
        inert: element.inert,
        ariaHidden: element.getAttribute('aria-hidden'),
        tabIndex: element.getAttribute('tabindex'),
      });
      element.inert = true;
      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('tabindex', '-1');
    };

    for (const child of Array.from(document.body.children)) {
      if (child instanceof HTMLElement) isolate(child);
    }

    // Merchant frameworks can append portals, dialogs, and notices after the
    // action opens. Isolate those late siblings too so they cannot become a
    // focus target or cover the authentication surface.
    this.isolationObserver = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of Array.from(record.addedNodes)) {
          if (node instanceof HTMLElement) isolate(node);
        }
      }
    });
    this.isolationObserver.observe(document.body, { childList: true });
  }

  private restoreMerchantPage(): void {
    this.isolationObserver?.disconnect();
    this.isolationObserver = null;
    for (const snapshot of this.isolatedSiblings.values()) {
      if (!snapshot.element.isConnected) continue;
      snapshot.element.inert = snapshot.inert;
      restoreAttribute(snapshot.element, 'aria-hidden', snapshot.ariaHidden);
      restoreAttribute(snapshot.element, 'tabindex', snapshot.tabIndex);
    }
    this.isolatedSiblings.clear();
  }
}

/**
 * Merchant-document side of a detached next action.
 *
 * Flow:
 * 1. A Payment or Express component reports `action_required`.
 * 2. The SDK gives one MessagePort to that component through the controller.
 * 3. This shell loads the Hosted Checkout action frame in the page top layer.
 * 4. After a source/origin/schema-validated FRAME_READY, it transfers the
 *    other port to the action frame exactly once.
 * 5. The hosted frame renders 3DS and exchanges provider data directly with
 *    the component; this shell handles presentation, cancellation, and errors.
 *
 * Payment authentication and success decisions remain outside this class.
 */
export class ElementsActionShell {
  private readonly host = new ElementsActionHost();
  private frame: HTMLIFrameElement | null = null;
  private action: ElementsActionRequest | null = null;
  private bootstrapTimeout: number | null = null;
  private removalTimeout: number | null = null;
  private previouslyFocused: HTMLElement | null = null;
  private componentPort: MessagePort | null = null;
  private actionFrameInitialized = false;
  private actionPresented = false;
  private lifecycleInterrupted = false;
  private returnWakeListenersAttached = false;

  constructor(
    private readonly hostedOrigin: string,
    private readonly onCancel: (actionId: string) => void,
    private readonly onError: (
      actionId: string,
      code: string,
      message: string,
      recoverable: boolean,
    ) => void,
    // The default is the production policy. Supplying a shorter deadline is an
    // internal test seam so timeout behavior can be proven without making a
    // reviewer wait through the complete payment-flow deadline.
    private readonly bootstrapTimeoutMs = ELEMENTS_ACTION_SHELL_POLICY.bootstrapTimeoutMs,
  ) {}

  isOpen(): boolean {
    return this.frame !== null;
  }

  show(action: ElementsActionRequest, componentPort: MessagePort): void {
    if (this.action?.id === action.id && this.frame) {
      componentPort.close();
      return;
    }
    this.removeFrame(false, false);

    this.action = action;
    this.componentPort = componentPort;
    this.actionFrameInitialized = false;
    this.actionPresented = false;
    this.lifecycleInterrupted = false;
    this.previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const frame = document.createElement('iframe');
    frame.id = `paypercut-elements-action-frame-${action.id}`;
    frame.title = '3D Secure authentication';
    frame.referrerPolicy = 'origin';
    frame.src = new URL('/elements/actions/three-d-secure', this.hostedOrigin).toString();
    // The ACS origin is not yet returned in the public next-action contract, so
    // WebAuthn delegation must remain broad until a trusted origin list is available.
    frame.allow = 'payment *; publickey-credentials-get *';
    frame.setAttribute('allowtransparency', 'true');
    frame.setAttribute('frameborder', '0');
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    frame.setAttribute('scrolling', 'no');
    Object.assign(frame.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      minWidth: '100%',
      minHeight: '100%',
      maxWidth: 'none',
      maxHeight: 'none',
      margin: '0',
      padding: '0',
      border: '0',
      display: 'block',
      overflow: 'hidden',
      background: ELEMENTS_ACTION_SHELL_POLICY.frameBackground,
      colorScheme: 'light only',
      opacity: '0',
      transition: `opacity ${ELEMENTS_ACTION_SHELL_POLICY.frameTransitionMs}ms ease-in-out`,
      willChange: 'opacity',
    });

    this.frame = frame;
    window.addEventListener('message', this.onWindowMessage);
    // Bootstrap the Hosted Checkout transport at full viewport dimensions, but
    // keep it non-interactive and invisible. Silent/frictionless 3DS must not
    // obscure or isolate the merchant page. The host is promoted to the top
    // layer only after the hosted frame reports genuine ACS or OOB content.
    const host = this.host.mount();
    host.replaceChildren(frame);

    this.bootstrapTimeout = window.setTimeout(() => {
      if (this.frame !== frame) return;
      this.onError(
        action.id,
        'elements_action_shell_timeout',
        'The secure authentication window could not be loaded.',
        true,
      );
    }, this.bootstrapTimeoutMs);

    let frameLoads = 0;
    frame.addEventListener('load', () => {
      if (this.frame !== frame) return;
      frameLoads += 1;
      if (frameLoads === 1) return;
      this.onError(
        action.id,
        'elements_submit_indeterminate',
        'The secure authentication window reloaded while its result was unknown.',
        false,
      );
    });
  }

  close(): void {
    const frame = this.frame;
    if (!frame) return;
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    frame.style.opacity = '0';
    this.host.beginClose();
    this.clearRemovalTimeout();
    this.removalTimeout = window.setTimeout(() => {
      if (this.frame === frame) this.removeFrame();
    }, ELEMENTS_ACTION_SHELL_POLICY.hostTransitionMs);
  }

  destroy(): void {
    this.removeFrame(true);
  }

  private readonly onWindowMessage = (event: MessageEvent): void => {
    const frame = this.frame;
    const action = this.action;
    // Window identity and origin are checked before parsing attacker-controlled
    // data. requestId correlation below then prevents a valid old action from
    // affecting the currently displayed action.
    if (
      !frame?.contentWindow ||
      !action ||
      event.source !== frame.contentWindow ||
      event.origin !== this.hostedOrigin
    ) {
      return;
    }

    if (isElementsActionFrameReadyMessage(event.data)) {
      if (this.actionFrameInitialized) return;
      this.clearBootstrapTimeout();
      const message: ElementsActionInitMessage = {
        channel: ELEMENTS_ACTION_CHANNEL,
        type: ElementsActionMessageType.Init,
        requestId: action.id,
        payload: {
          source: action.source ?? 'component',
          actionId: action.id,
          actionType: action.type,
        },
      };
      const componentPort = this.componentPort;
      if (!componentPort) {
        this.onError(
          action.id,
          'elements_action_channel_unavailable',
          'The secure authentication channel is unavailable.',
          true,
        );
        return;
      }
      frame.contentWindow.postMessage(message, this.hostedOrigin, [componentPort]);
      this.componentPort = null;
      this.actionFrameInitialized = true;
      this.attachReturnWakeListeners();
      window.requestAnimationFrame(() => {
        if (this.frame === frame) {
          frame.style.opacity = '1';
        }
      });
      return;
    }

    if (
      (isElementsActionPhaseMessage(event.data) ||
        isElementsActionResultMessage(event.data) ||
        isElementsActionErrorMessage(event.data)) &&
      event.data.requestId !== action.id
    ) {
      return;
    }

    if (isElementsActionPhaseMessage(event.data)) {
      frame.dataset.paypercutActionPhase = event.data.payload.phase;
      if (event.data.payload.phase === 'content_ready' && !this.actionPresented) {
        this.actionPresented = true;
        frame.removeAttribute('aria-hidden');
        frame.tabIndex = 0;
        this.host.open();
        window.requestAnimationFrame(() => {
          if (this.frame === frame && this.actionPresented) {
            frame.focus({ preventScroll: true });
          }
        });
      }
      return;
    }
    if (isElementsActionResultMessage(event.data)) {
      if (event.data.payload.status === 'canceled') this.onCancel(action.id);
      return;
    }
    if (isElementsActionErrorMessage(event.data)) {
      this.onError(
        action.id,
        event.data.payload.code,
        event.data.payload.message,
        event.data.payload.recoverable,
      );
    }
  };

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') {
      this.lifecycleInterrupted = true;
      return;
    }
    this.sendReturnWake('visibility');
  };

  private readonly onPageHide = (): void => {
    this.lifecycleInterrupted = true;
  };

  private readonly onPageShow = (): void => {
    this.sendReturnWake('pageshow');
  };

  private readonly onOffline = (): void => {
    this.lifecycleInterrupted = true;
  };

  private readonly onOnline = (): void => {
    this.sendReturnWake('online');
  };

  private readonly onWindowFocus = (): void => {
    if (this.lifecycleInterrupted) this.sendReturnWake('focus');
  };

  private sendReturnWake(source: ElementsActionWakeSource): void {
    const frame = this.frame;
    const action = this.action;
    if (
      !this.actionFrameInitialized ||
      !frame?.contentWindow ||
      !action ||
      document.visibilityState !== 'visible'
    ) {
      return;
    }
    const message = createElementsActionWakeMessage(action.id, source);
    frame.contentWindow.postMessage(message, this.hostedOrigin);
    this.lifecycleInterrupted = false;
  }

  private attachReturnWakeListeners(): void {
    if (this.returnWakeListenersAttached) return;
    this.returnWakeListenersAttached = true;
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pageshow', this.onPageShow);
    window.addEventListener('offline', this.onOffline);
    window.addEventListener('online', this.onOnline);
    window.addEventListener('focus', this.onWindowFocus);
  }

  private detachReturnWakeListeners(): void {
    if (!this.returnWakeListenersAttached) return;
    this.returnWakeListenersAttached = false;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('offline', this.onOffline);
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('focus', this.onWindowFocus);
  }

  private removeFrame(removeHost = false, restoreFocus = true): void {
    const frame = this.frame;
    this.frame = null;
    this.action = null;
    this.componentPort?.close();
    this.componentPort = null;
    this.actionFrameInitialized = false;
    this.lifecycleInterrupted = false;
    this.detachReturnWakeListeners();
    this.clearBootstrapTimeout();
    this.clearRemovalTimeout();
    window.removeEventListener('message', this.onWindowMessage);

    frame?.remove();
    if (removeHost) this.host.destroy();
    else this.host.finishClose();

    const focusTarget = this.previouslyFocused;
    this.previouslyFocused = null;
    if (restoreFocus && focusTarget?.isConnected) {
      try {
        focusTarget.focus({ preventScroll: true });
      } catch {
        focusTarget.focus();
      }
    }
  }

  private clearBootstrapTimeout(): void {
    if (this.bootstrapTimeout === null) return;
    window.clearTimeout(this.bootstrapTimeout);
    this.bootstrapTimeout = null;
  }

  private clearRemovalTimeout(): void {
    if (this.removalTimeout === null) return;
    window.clearTimeout(this.removalTimeout);
    this.removalTimeout = null;
  }
}
